import { useState, useEffect, useRef, useCallback } from 'react';
import {
  MessageCircle, Send, ArrowLeft, User, Users, Loader2, Plus,
  Check, CheckCheck, Circle, X
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { chatAPI, friendAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { devError } from '../lib/devLog';

const EnhancedChatDialog = ({ open, onOpenChange, recipientId, recipientName }) => {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [typingUsers, setTypingUsers] = useState([]);
  const [showNewGroup, setShowNewGroup] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [friends, setFriends] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  useEffect(() => {
    if (open) {
      fetchConversations();
      if (recipientId) openDirectChat(recipientId, recipientName);
    }
  }, [open, recipientId]);

  useEffect(() => {
    if (selectedConv) {
      fetchMessages(selectedConv.id);
      const msgInterval = setInterval(() => fetchMessages(selectedConv.id), 3000);
      const typingInterval = setInterval(() => fetchTyping(selectedConv.id), 2000);
      return () => { clearInterval(msgInterval); clearInterval(typingInterval); };
    }
  }, [selectedConv?.id]);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const fetchConversations = async () => {
    try {
      const res = await chatAPI.getEnhancedConversations();
      setConversations(res.data);
    } catch (e) { devError(e); }
    finally { setLoading(false); }
  };

  const fetchMessages = async (convId) => {
    try {
      const res = await chatAPI.getChatMessages(convId);
      setMessages(res.data);
    } catch (e) { devError(e); }
  };

  const fetchTyping = async (convId) => {
    try {
      const res = await chatAPI.getTyping(convId);
      setTypingUsers(res.data.typing || []);
    } catch (e) { /* ignore */ }
  };

  const openDirectChat = async (targetId, targetName) => {
    const existing = conversations.find(c =>
      c.type !== 'group' && c.participant_ids.includes(targetId)
    );
    if (existing) {
      setSelectedConv(existing);
    } else {
      setSelectedConv({
        id: 'new', type: 'direct',
        participant_ids: [user.id, targetId],
        participant_names: { [user.id]: user.name, [targetId]: targetName },
        recipientId: targetId
      });
      setMessages([]);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    setSending(true);
    try {
      if (selectedConv.type === 'group' && selectedConv.id !== 'new') {
        await chatAPI.sendGroupMessage(selectedConv.id, newMessage.trim());
      } else {
        const recipId = selectedConv.id === 'new'
          ? selectedConv.recipientId
          : selectedConv.participant_ids.find(id => id !== user.id);
        const res = await chatAPI.sendChatMessage(recipId, newMessage.trim());
        if (selectedConv.id === 'new') {
          setSelectedConv(prev => ({ ...prev, id: res.data.conversation_id }));
          fetchConversations();
        }
      }
      setNewMessage('');
      if (selectedConv.id !== 'new') fetchMessages(selectedConv.id);
      // Stop typing
      chatAPI.sendTyping(selectedConv.id, false).catch(() => {});
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send message');
    } finally { setSending(false); }
  };

  const handleTyping = (value) => {
    setNewMessage(value);
    if (selectedConv?.id && selectedConv.id !== 'new') {
      chatAPI.sendTyping(selectedConv.id, true).catch(() => {});
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => {
        chatAPI.sendTyping(selectedConv.id, false).catch(() => {});
      }, 3000);
    }
  };

  const handleCreateGroup = async () => {
    if (!groupName.trim() || selectedMembers.length === 0) return;
    try {
      const res = await chatAPI.createGroup(groupName.trim(), selectedMembers);
      setShowNewGroup(false);
      setGroupName('');
      setSelectedMembers([]);
      fetchConversations();
      setSelectedConv(res.data);
      toast.success('Group created!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create group');
    }
  };

  const loadFriends = async () => {
    try {
      const res = await friendAPI.getFriends();
      setFriends(res.data);
    } catch (e) { devError(e); }
  };

  const getConvName = (conv) => {
    if (conv.type === 'group') return conv.name || 'Group Chat';
    const otherId = conv.participant_ids.find(id => id !== user?.id);
    return conv.participant_names?.[otherId] || 'Chat';
  };

  const getConvIcon = (conv) => {
    if (conv.type === 'group') return Users;
    return User;
  };

  const formatTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const getReadStatus = (msg) => {
    if (msg.sender_id !== user?.id) return null;
    const readBy = msg.read_by || [];
    if (selectedConv?.type === 'group') {
      const otherMembers = (selectedConv.participant_ids || []).filter(id => id !== user.id);
      const allRead = otherMembers.every(id => readBy.includes(id));
      return allRead ? 'read' : 'sent';
    }
    return readBy.length > 1 || msg.is_read ? 'read' : 'sent';
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg h-[650px] flex flex-col p-0">
        <DialogHeader className="px-4 py-3 border-b border-zinc-100 flex-shrink-0">
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {showNewGroup ? (
              <>
                <Button variant="ghost" size="sm" onClick={() => setShowNewGroup(false)}><ArrowLeft className="w-4 h-4" /></Button>
                <span>New Group</span>
              </>
            ) : selectedConv ? (
              <>
                <Button variant="ghost" size="sm" onClick={() => { setSelectedConv(null); setTypingUsers([]); }}><ArrowLeft className="w-4 h-4" /></Button>
                <div className="flex items-center gap-2">
                  {selectedConv.type === 'group' ? (
                    <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                      <Users className="w-4 h-4 text-purple-600" />
                    </div>
                  ) : (
                    <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                      <User className="w-4 h-4 text-orange-600" />
                    </div>
                  )}
                  <span className="text-sm font-semibold">{getConvName(selectedConv)}</span>
                </div>
              </>
            ) : (
              <>
                <MessageCircle className="w-5 h-5 text-orange-500" />
                <span>Messages</span>
                <div className="ml-auto">
                  <Button
                    variant="ghost" size="sm"
                    onClick={() => { setShowNewGroup(true); loadFriends(); }}
                    className="text-orange-500"
                    data-testid="new-group-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Group
                  </Button>
                </div>
              </>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col min-h-0">
          {showNewGroup ? (
            /* Create Group Form */
            <div className="p-4 space-y-4 overflow-y-auto">
              <Input
                placeholder="Group name..."
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                data-testid="group-name-input"
              />
              <p className="text-sm font-medium text-zinc-700">Add Friends</p>
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {friends.map(f => (
                  <label key={f.friend_id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-zinc-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedMembers.includes(f.friend_id)}
                      onChange={(e) => {
                        if (e.target.checked) setSelectedMembers(prev => [...prev, f.friend_id]);
                        else setSelectedMembers(prev => prev.filter(id => id !== f.friend_id));
                      }}
                      className="w-4 h-4 text-orange-500 rounded"
                    />
                    <div className="w-8 h-8 bg-zinc-100 rounded-full flex items-center justify-center">
                      <User className="w-4 h-4 text-zinc-500" />
                    </div>
                    <span className="text-sm font-medium">{f.friend_name}</span>
                  </label>
                ))}
                {friends.length === 0 && (
                  <p className="text-sm text-zinc-400 text-center py-4">Add friends first to create a group</p>
                )}
              </div>
              <Button
                onClick={handleCreateGroup}
                disabled={!groupName.trim() || selectedMembers.length === 0}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white"
                data-testid="create-group-btn"
              >
                Create Group ({selectedMembers.length} members)
              </Button>
            </div>
          ) : !selectedConv ? (
            /* Conversation List */
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
                </div>
              ) : conversations.length === 0 ? (
                <div className="text-center py-12 px-4">
                  <MessageCircle className="w-12 h-12 text-zinc-300 mx-auto mb-3" />
                  <p className="text-zinc-600 font-medium">No conversations yet</p>
                  <p className="text-sm text-zinc-400 mt-1">Add friends and start chatting!</p>
                </div>
              ) : (
                <div className="divide-y divide-zinc-100">
                  {conversations.map(conv => {
                    const ConvIcon = getConvIcon(conv);
                    return (
                      <button
                        key={conv.id}
                        onClick={() => setSelectedConv(conv)}
                        className="w-full px-4 py-3 flex items-center gap-3 hover:bg-zinc-50 transition-colors text-left"
                        data-testid={`conv-${conv.id}`}
                      >
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${conv.type === 'group' ? 'bg-purple-100' : 'bg-orange-100'}`}>
                          <ConvIcon className={`w-5 h-5 ${conv.type === 'group' ? 'text-purple-600' : 'text-orange-600'}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-zinc-900 truncate">{getConvName(conv)}</p>
                            {conv.type === 'group' && (
                              <span className="text-xs bg-purple-100 text-purple-600 px-1.5 py-0.5 rounded">Group</span>
                            )}
                          </div>
                          <p className="text-sm text-zinc-500 truncate">{conv.last_message || 'No messages yet'}</p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          {conv.last_message_at && (
                            <p className="text-xs text-zinc-400">{formatTime(conv.last_message_at)}</p>
                          )}
                          {conv.unread_count > 0 && (
                            <span className="inline-flex items-center justify-center w-5 h-5 bg-orange-500 text-white text-xs rounded-full mt-1">
                              {conv.unread_count}
                            </span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            /* Message View */
            <>
              <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
                {messages.map(msg => {
                  const isMine = msg.sender_id === user?.id;
                  const readStatus = getReadStatus(msg);
                  return (
                    <div key={msg.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[75%] ${isMine ? '' : 'flex gap-2'}`}>
                        {!isMine && selectedConv?.type === 'group' && (
                          <div className="w-6 h-6 bg-zinc-200 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                            <User className="w-3 h-3 text-zinc-500" />
                          </div>
                        )}
                        <div>
                          {!isMine && selectedConv?.type === 'group' && (
                            <p className="text-xs text-zinc-400 mb-0.5 ml-1">{msg.sender_name}</p>
                          )}
                          <div className={`rounded-2xl px-4 py-2 ${isMine ? 'bg-orange-500 text-white' : 'bg-zinc-100 text-zinc-900'}`}>
                            <p className="text-sm">{msg.content}</p>
                            <div className={`flex items-center justify-end gap-1 mt-0.5 ${isMine ? 'text-orange-200' : 'text-zinc-400'}`}>
                              <span className="text-[10px]">{formatTime(msg.created_at)}</span>
                              {readStatus === 'read' && <CheckCheck className="w-3 h-3" />}
                              {readStatus === 'sent' && <Check className="w-3 h-3" />}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Typing indicator */}
                {typingUsers.length > 0 && (
                  <div className="flex items-center gap-2 px-2">
                    <div className="bg-zinc-100 rounded-2xl px-4 py-2 flex items-center gap-2">
                      <div className="flex gap-0.5">
                        <Circle className="w-1.5 h-1.5 fill-zinc-400 text-zinc-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                        <Circle className="w-1.5 h-1.5 fill-zinc-400 text-zinc-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                        <Circle className="w-1.5 h-1.5 fill-zinc-400 text-zinc-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                      <span className="text-xs text-zinc-500">
                        {typingUsers.map(t => t.user_name).join(', ')} typing...
                      </span>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Message Input */}
              <form onSubmit={handleSend} className="p-3 border-t border-zinc-100 flex-shrink-0">
                <div className="flex gap-2">
                  <Input
                    value={newMessage}
                    onChange={(e) => handleTyping(e.target.value)}
                    placeholder="Type a message..."
                    className="flex-1"
                    disabled={sending}
                    data-testid="chat-message-input"
                  />
                  <Button
                    type="submit"
                    disabled={!newMessage.trim() || sending}
                    className="bg-orange-500 hover:bg-orange-600 text-white"
                    data-testid="chat-send-btn"
                  >
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
              </form>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EnhancedChatDialog;
